import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { BaseOneResponseInterface } from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ILoadBalancerChannel } from '../../../../view/settings/api-automation-settings/load-balancer-channels/load-balancer-channels.model';
import { LoadBalancerChannelService } from './load-balancer-channels.service';
import {
  ActionTypes,
  LoadBalancerChannelsLoading,
  LoadBalancerChannelsLoaded,
  FetchError,
  GetLoadBalancerChannel,
  GetLoadBalancerChannelCompleted,
  EditLoadBalancerChannel,
  EditLoadBalancerChannelCompleted,
} from './load-balancer-channels.actions';

@Injectable()
export class LoadBalancerChannelEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: LoadBalancerChannelService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.LOAD_BALANCER_CHANNELS_DATA_LOADING),
      switchMap((payload: LoadBalancerChannelsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,ASC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [{ name: { $cont: searchText } }, { description: { $cont: searchText } }],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        // Always served in ascending id order; this list is not user-sortable.

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new LoadBalancerChannelsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_LOAD_BALANCER_CHANNEL),
      switchMap((objectData: GetLoadBalancerChannel) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<ILoadBalancerChannel>) =>
            of(new GetLoadBalancerChannelCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_LOAD_BALANCER_CHANNEL),
      switchMap((objectData: EditLoadBalancerChannel) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ILoadBalancerChannel>) =>
            of(
              new EditLoadBalancerChannelCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );
}
